import { View, StyleSheet } from "react-native";
import { Row, Column } from "ui-grid";
import { domusoResponsiveStyleHelper } from "../../../screens/new/utils/domuso-responsive";
import {
  DoubleStatusBadge,
  NavigationTextIconButton,
  Text,
  theme,
  typography,
} from "ui-components";
import { useNavigation } from "@react-navigation/native";
import { translate } from "../../../i18n/";
import automationTestingLocators from "../../../screens/new/utils/automation-testing-locators.json";

export interface RewardsHeaderProps {
  testID: string;
  accessibilityLabel: string;
  onPresGoBack?: any;
  currentPoints?: string;
  totalPoints?: string;
}

export const RewardsHeader = (props: RewardsHeaderProps) => {
  const { testID, accessibilityLabel, onPresGoBack, currentPoints, totalPoints } = props;
  const navigation = useNavigation();

  return (
    <>
      <Row>
        <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
        <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={[styles.cardTop]}>
          <View style={styles.inputRowInner}>
            <View style={styles.cardInnerView}>
              <View style={styles.headerContainer}>
                <View style={{ flex: 1 }}>
                  <Text
                    style={styles.welcomeTextStyle1}
                    testID={
                      testID +
                      automationTestingLocators.rewardsHeader.rewardsHeaderWelcomeText1.testID
                    }
                    accessibilityLabel={
                      accessibilityLabel +
                      automationTestingLocators.rewardsHeader.rewardsHeaderWelcomeText1
                        .accessibilityLabel
                    }>
                    {translate("rewardsHeader.welcomeText1")}
                  </Text>
                  <Text
                    style={styles.welcomeTextStyle2}
                    testID={
                      testID +
                      automationTestingLocators.rewardsHeader.rewardsHeaderWelcomeText2.testID
                    }
                    accessibilityLabel={
                      accessibilityLabel +
                      automationTestingLocators.rewardsHeader.rewardsHeaderWelcomeText2
                        .accessibilityLabel
                    }>
                    {translate("rewardsHeader.welcomeText2")}
                  </Text>
                </View>
                <View>
                  <NavigationTextIconButton
                    testID={
                      testID +
                      automationTestingLocators.rewardsHeader.rewardsHeaderGoBackButton.testID
                    }
                    accessibilityLabel={
                      accessibilityLabel +
                      automationTestingLocators.rewardsHeader.rewardsHeaderGoBackButton
                        .accessibilityLabel
                    }
                    text={translate("rewardsHeader.goBack") as string}
                    iconName="direction-left"
                    onPress={() => {
                      if (onPresGoBack) {
                        return onPresGoBack();
                      }
                      navigation?.goBack();
                    }}
                  />
                </View>
              </View>
            </View>
          </View>
        </Column>
        <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
      </Row>

      <Row>
        <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
        <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.inputRow}>
          <View style={styles.inputRowInner}>
            <View style={{ marginTop: -25 }}>
              <DoubleStatusBadge
                leftStatusContainerStyle={{ marginTop: 5 }}
                rightStatusContainerStyle={{ marginTop: 5 }}
                leftIconSize={30}
                rightIconSize={30}
                leftIconName="gift"
                rightIconName="gift"
                leftHeader={translate("rewardsHeader.currentPointsHeader") as string}
                leftStatus={currentPoints}
                rightHeader={translate("rewardsHeader.totalPointHeader") as string}
                rightStatus={totalPoints}
                testID={
                  testID +
                  automationTestingLocators.rewardsHeader
                    .rewardsHeaderCurrentPointsAndTotalPointsDoubleStatusBadge.testID
                }
                accessibilityLabel={
                  accessibilityLabel +
                  automationTestingLocators.rewardsHeader
                    .rewardsHeaderCurrentPointsAndTotalPointsDoubleStatusBadge.accessibilityLabel
                }
              />
            </View>
          </View>
        </Column>
        <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
      </Row>
    </>
  );
};

const styles = StyleSheet.create({
  cardTop: {
    ...domusoResponsiveStyleHelper({
      webPhone: { backgroundColor: theme.colors.primary },
      webTablet: {
        backgroundColor: theme.colors.primary,
        paddingLeft: 40,
        paddingRight: 40,
        borderTopRightRadius: 10,
        borderTopLeftRadius: 10,
        borderTopWidth: 1,
        borderTopColor: "rgba(102, 102, 102, 0.1)",
        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
      webDesktop: {
        backgroundColor: theme.colors.primary,
        paddingLeft: 40,
        paddingRight: 40,
        borderTopRightRadius: 10,
        borderTopLeftRadius: 10,
        borderTopWidth: 1,
        borderTopColor: "rgba(102, 102, 102, 0.1)",
        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
      iosPhone: { backgroundColor: theme.colors.primary },
      iosTablet: {
        backgroundColor: theme.colors.primary,
        paddingLeft: 40,
        paddingRight: 40,
        borderTopRightRadius: 10,
        borderTopLeftRadius: 10,
        borderTopWidth: 1,
        borderTopColor: "rgba(102, 102, 102, 0.1)",
        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
      androidPhone: { backgroundColor: theme.colors.primary },
      androidTablet: {
        backgroundColor: theme.colors.primary,
        paddingLeft: 40,
        paddingRight: 40,
        borderTopRightRadius: 10,
        borderTopLeftRadius: 10,
        borderTopWidth: 1,
        borderTopColor: "rgba(102, 102, 102, 0.1)",
        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
    }),
    paddingLeft: 0,
    paddingRight: 0,
    paddingHorizontal: 0,
  },
  inputRow: {
    ...domusoResponsiveStyleHelper({
      webPhone: {},
      webTablet: {
        paddingLeft: 40,
        paddingRight: 40,

        backgroundColor: "#F5F5F5",

        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
      webDesktop: {
        paddingLeft: 40,
        paddingRight: 40,

        backgroundColor: "#F5F5F5",

        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
      iosPhone: {},
      iosTablet: {
        paddingLeft: 40,
        paddingRight: 40,

        backgroundColor: "#F5F5F5",

        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
      androidPhone: {},
      androidTablet: {
        paddingLeft: 40,
        paddingRight: 40,

        backgroundColor: "#F5F5F5",

        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
    }),
  },
  inputRowInner: {
    ...domusoResponsiveStyleHelper({
      webPhone: {},
      webTablet: {
        paddingLeft: 0,
        paddingRight: 0,
      },
      webDesktop: {
        paddingLeft: 30,
        paddingRight: 30,
      },
      iosPhone: {},
      iosTablet: {
        paddingLeft: 0,
        paddingRight: 0,
      },
      androidPhone: {},
      androidTablet: {
        paddingLeft: 0,
        paddingRight: 0,
      },
    }),
  },
  cardInnerView: {
    flex: 1,
    ...domusoResponsiveStyleHelper({
      webPhone: { paddingHorizontal: 15, paddingVertical: 20 },
      webTablet: { paddingHorizontal: 40, paddingVertical: 20 },
      webDesktop: { paddingHorizontal: 40, paddingVertical: 20 },
      iosPhone: { paddingHorizontal: 15, paddingVertical: 20 },
      iosTablet: { paddingHorizontal: 40, paddingVertical: 20 },
      androidPhone: { paddingHorizontal: 15, paddingVertical: 20 },
      androidTablet: { paddingHorizontal: 40, paddingVertical: 20 },
    }),
    paddingBottom: 38,
  },
  headerContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    flex: 1,
  },
  welcomeTextStyle1: {
    ...typography.title2,
    color: theme.colors.white,
  },
  welcomeTextStyle2: {
    ...typography.heading2,
    color: theme.colors.white,
    lineHeight: 50,
    fontWeight: "400",
  },
});
